import { useId, useMemo, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableRowClickEvent, DataTableSortEvent } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { SelectButton } from 'primereact/selectbutton'
import InfoTip from '../../../components/novarium/InfoTip'
import {
  UrgencyDimensionRowDTO,
  UrgencyStateKey,
  UrgencyStateRowDTO,
  UrgencySummaryDTO,
} from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { UrgencyDrill } from '../urgencyDetailState'
import { ATTENDED_STATE_GROUPS, URGENCY_STATE_GROUP_ID, URGENCY_STATE_MESSAGE_ID } from '../urgencyStateGroups'
import { formatCount, formatMinutes, formatPercent } from '../urgencySummaryFormat'

/** Cómo se agrupan las atenciones de la tabla. Es el mismo dato visto de otra manera (SelectButton, no pestañas). */
export type UrgencyGroupBy = 'template' | 'financier' | 'professional' | 'state'

/** Las agrupaciones en el orden en que se ofrecen. */
const GROUP_BY_OPTIONS: readonly { value: UrgencyGroupBy; messageId: string }[] = [
  { value: 'template', messageId: 'UrgencySummaryGroupByTemplate' },
  { value: 'financier', messageId: 'UrgencySummaryGroupByFinancier' },
  { value: 'professional', messageId: 'UrgencySummaryGroupByProfessional' },
  { value: 'state', messageId: 'UrgencySummaryGroupByState' },
]

/** Cuántas filas se ven antes de «Ver todos (N)». */
export const TOP_ROWS = 10

interface SortState {
  field: string
  order: 1 | -1
}

/** Orden inicial de cada agrupamiento: lo que más pacientes tiene, primero. */
function defaultSort(groupBy: UrgencyGroupBy): SortState {
  return { field: groupBy === 'professional' ? 'attended' : 'arrivals', order: -1 }
}

function compareRows(a: UrgencyDimensionRowDTO, b: UrgencyDimensionRowDTO, sort: SortState): number {
  if (sort.field === 'name') return sort.order * a.name.localeCompare(b.name, 'es')
  const field = sort.field as 'arrivals' | 'attended' | 'leftWithoutAttention' | 'leftPercent' | 'waitedAvgMinutes'
  const left = a[field]
  const right = b[field]
  // Sin dato, siempre al final
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return sort.order * (left - right)
}

function stateName(intl: IntlShape, row: UrgencyStateRowDTO): string {
  const messageId = URGENCY_STATE_MESSAGE_ID[row.key] as string | undefined
  return messageId ? intl.formatMessage({ id: messageId }) : row.name
}

interface UrgencyGroupedTableProps {
  summary: UrgencySummaryDTO
  groupBy: UrgencyGroupBy
  onGroupByChange: (groupBy: UrgencyGroupBy) => void
  /** Las agrupaciones que se ofrecen: «Guardia» no, si ya se eligió una guardia (o el sitio tiene una sola). */
  availableGroups: readonly UrgencyGroupBy[]
  onDrill: (drill: UrgencyDrill) => void
}

/**
 * Las atenciones del período agrupadas por guardia, financiador, profesional o estado, en una sola tabla. Está completa
 * en memoria: el buscador por nombre y el orden por columna se resuelven acá. Se ven las 10 primeras filas y «Ver todos
 * (N)» muestra el resto. Cada fila tiene una sola acción: ver sus atenciones en el detalle (el ojo, o un click en la fila).
 * En «Profesional», el ⓘ de la cabecera explica a quién se atribuye cada atención.
 */
export default function UrgencyGroupedTable({ summary, groupBy, onGroupByChange, availableGroups, onDrill }: UrgencyGroupedTableProps) {
  const intl = useIntl()
  const labelId = useId()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>(() => defaultSort(groupBy))
  const [showAll, setShowAll] = useState(false)

  const groupOptions = GROUP_BY_OPTIONS
    .filter((option) => availableGroups.includes(option.value))
    .map((option) => ({ value: option.value, label: intl.formatMessage({ id: option.messageId }) }))

  function changeGroupBy(next: UrgencyGroupBy) {
    setSearch('')
    setSort(defaultSort(next))
    setShowAll(false)
    onGroupByChange(next)
  }

  function handleSort(event: DataTableSortEvent) {
    setSort({ field: event.sortField, order: event.sortOrder === 1 ? 1 : -1 })
  }

  const allRows: UrgencyDimensionRowDTO[] = groupBy === 'template'
    ? summary.byTemplate
    : groupBy === 'financier' ? summary.byFinancier : summary.byProfessional
  const searching = search.trim() !== ''
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('es')
    const matching = needle === '' ? allRows : allRows.filter((row) => row.name.toLocaleLowerCase('es').includes(needle))
    return [...matching].sort((a, b) => compareRows(a, b, sort))
  }, [allRows, search, sort])
  // Con una búsqueda se ven todas las que coinciden: el tope es para recorrer la lista, no para ocultar resultados
  const shownRows = searching || showAll ? rows : rows.slice(0, TOP_ROWS)
  const hasMore = !searching && rows.length > TOP_ROWS

  const nameHeaderId = groupBy === 'template' ? 'UrgencySummaryTemplate'
    : groupBy === 'financier' ? 'UrgencySummaryFinancier' : 'UrgencySummaryColProfessional'

  /** Una fila sin guardia ("N/D") no se puede abrir en el detalle: no hay a qué filtrar. Sin financiador o sin profesional sí. */
  const rowOpensDetail = (row: UrgencyDimensionRowDTO) => groupBy !== 'template' || row.id !== null

  function drillRow(row: UrgencyDimensionRowDTO) {
    if (groupBy === 'template' && row.id !== null) {
      onDrill({ templateId: row.id, templateName: row.name })
    } else if (groupBy === 'financier') {
      onDrill({ financierId: row.id, financierName: row.id === null ? null : row.name, noFinancier: row.id === null })
    } else if (groupBy === 'professional') {
      onDrill({ professionalName: row.id === null ? null : row.name, noProfessional: row.id === null, stateGroups: ATTENDED_STATE_GROUPS })
    }
  }

  function drillState(row: UrgencyStateRowDTO) {
    onDrill({ stateGroups: [URGENCY_STATE_GROUP_ID[row.key]] })
  }

  // Cómo se atribuye cada atención a un profesional se lee en el ⓘ de la cabecera (sin ordenar la tabla al tocarlo)
  const nameLabel = intl.formatMessage({ id: nameHeaderId })
  const nameHeader = groupBy === 'professional'
    ? (
      <span className="nv-urg-th-tip">
        {nameLabel}
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryProfessionalNote' })} />
        </span>
      </span>
    )
    : nameLabel

  const centered = { className: 'nv-urg-center', headerClassName: 'nv-urg-center' }
  const detailHeader = <span className="nv-sr-only">{intl.formatMessage({ id: 'UrgencySummaryViewDetail' })}</span>

  function detailButton(name: string, onClick: () => void) {
    return (
      <Button
        type="button"
        text
        className="nv-icon nv-tip"
        icon="pi pi-eye"
        aria-label={intl.formatMessage({ id: 'UrgencySummaryViewDetailOf' }, { name })}
        data-pr-tooltip={intl.formatMessage({ id: 'UrgencySummaryViewDetail' })}
        onClick={(event) => {
          event.stopPropagation()
          onClick()
        }}
      />
    )
  }

  function renderStateTable() {
    return (
      <div className="nv-load-table">
        <DataTable
          value={summary.byState}
          dataKey="key"
          rowClassName={() => ({ 'nv-urg-clickable': true })}
          onRowClick={(event: DataTableRowClickEvent) => drillState(event.data as UrgencyStateRowDTO)}
        >
          <Column header={intl.formatMessage({ id: 'UrgencySummaryColState' })} body={(row: UrgencyStateRowDTO) => stateName(intl, row)} />
          <Column
            header={intl.formatMessage({ id: 'UrgencySummaryColQuantity' })}
            {...centered}
            body={(row: UrgencyStateRowDTO) => formatCount(intl, row.total)}
          />
          <Column
            header={intl.formatMessage({ id: 'UrgencySummaryColWait' })}
            {...centered}
            body={(row: UrgencyStateRowDTO) => formatMinutes(intl, row.waitedAvgMinutes)}
          />
          <Column
            header={detailHeader}
            className="nv-col-actions"
            headerClassName="nv-col-actions"
            body={(row: UrgencyStateRowDTO) => detailButton(stateName(intl, row), () => drillState(row))}
          />
        </DataTable>
      </div>
    )
  }

  function renderDimensionTable() {
    const isProfessional = groupBy === 'professional'
    return (
      <div className="nv-load-table">
        <DataTable
          // Al cambiar de agrupamiento las columnas son otras
          key={groupBy}
          value={shownRows}
          lazy
          sortField={sort.field}
          sortOrder={sort.order}
          onSort={handleSort}
          emptyMessage={intl.formatMessage({ id: 'UrgencySummaryGroupNoMatches' })}
          rowClassName={(row: UrgencyDimensionRowDTO) => ({ 'nv-urg-clickable': rowOpensDetail(row) })}
          onRowClick={(event: DataTableRowClickEvent) => {
            const row = event.data as UrgencyDimensionRowDTO
            if (rowOpensDetail(row)) drillRow(row)
          }}
        >
          <Column field="name" header={nameHeader} sortable />
          {!isProfessional && (
            <Column
              field="arrivals"
              header={intl.formatMessage({ id: 'UrgencySummaryColArrivals' })}
              sortable
              {...centered}
              body={(row: UrgencyDimensionRowDTO) => formatCount(intl, row.arrivals)}
            />
          )}
          <Column
            field="attended"
            header={intl.formatMessage({ id: 'UrgencySummaryColAttended' })}
            sortable
            {...centered}
            body={(row: UrgencyDimensionRowDTO) => formatCount(intl, row.attended)}
          />
          {!isProfessional && (
            <Column
              field="leftWithoutAttention"
              header={intl.formatMessage({ id: 'UrgencySummaryColLeft' })}
              sortable
              {...centered}
              body={(row: UrgencyDimensionRowDTO) => formatCount(intl, row.leftWithoutAttention)}
            />
          )}
          {!isProfessional && (
            <Column
              field="leftPercent"
              header={intl.formatMessage({ id: 'UrgencySummaryColLeftPercent' })}
              sortable
              {...centered}
              body={(row: UrgencyDimensionRowDTO) => formatPercent(intl, row.leftPercent)}
            />
          )}
          <Column
            field="waitedAvgMinutes"
            header={intl.formatMessage({ id: 'UrgencySummaryColWait' })}
            sortable
            {...centered}
            body={(row: UrgencyDimensionRowDTO) => formatMinutes(intl, row.waitedAvgMinutes)}
          />
          <Column
            header={detailHeader}
            className="nv-col-actions"
            headerClassName="nv-col-actions"
            body={(row: UrgencyDimensionRowDTO) => rowOpensDetail(row) && detailButton(row.name, () => drillRow(row))}
          />
        </DataTable>
      </div>
    )
  }

  return (
    <div className="nv-urg-group">
      <div className="nv-urg-toolbar">
        <div className="nv-urg-group-by">
          <span id={labelId} className="nv-strong">{intl.formatMessage({ id: 'UrgencySummaryGroupBy' })}</span>
          <SelectButton
            value={groupBy}
            options={groupOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(event) => { if (event.value) changeGroupBy(event.value as UrgencyGroupBy) }}
            aria-labelledby={labelId}
          />
        </div>
        {groupBy !== 'state' && (
          <span className="p-input-icon-left">
            <i className="pi pi-search" aria-hidden="true" />
            <InputText
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={intl.formatMessage({ id: 'UrgencySummaryGroupSearch' })}
              aria-label={intl.formatMessage({ id: 'UrgencySummaryGroupSearch' })}
            />
          </span>
        )}
      </div>

      {groupBy === 'state' ? renderStateTable() : allRows.length === 0
        ? <p className="nv-urg-empty">{intl.formatMessage({ id: 'UrgencySummaryEmpty' })}</p>
        : renderDimensionTable()}

      {groupBy !== 'state' && hasMore && (
        <div className="nv-urg-more">
          <Button
            type="button"
            link
            label={intl.formatMessage(
              { id: showAll ? 'UrgencySummaryShowLess' : 'UrgencySummaryShowAll' },
              { count: rows.length, countText: formatCount(intl, rows.length), top: TOP_ROWS },
            )}
            aria-expanded={showAll}
            onClick={() => setShowAll((value) => !value)}
          />
        </div>
      )}
    </div>
  )
}
